import React, { useState, useMemo } from 'react';
import { useClientData } from '../../../hooks';
import { RollingWindow } from '../../../types/common';
import AppLayout from '../../layouts/AppLayout';
import ClientAnalytics from './index';

type PeriodMode = 'fy_india' | 'calendar';

export const ClientAnalyticsPage: React.FC = () => {
  const [rollingWindow, setRollingWindow] = useState<RollingWindow>(12);
  const [periodMode, setPeriodMode] = useState<PeriodMode>('fy_india');
  const [selectedFYs, setSelectedFYs] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);

  // First load all data to derive available years
  const { clients: allClients, monthlyRows: allMonthlyRows, invoicesByYM: allInvoices, summaryPeriod, loading } = useClientData(rollingWindow, [], []);

  // Derive available FY and calendar years from unfiltered data
  const { fyOptions, calendarYearOptions } = useMemo(() => {
    const fys = new Set<string>();
    const years = new Set<string>();

    allMonthlyRows.forEach(row => {
      const [y, m] = row.ym.split('-').map(Number);
      years.add(String(y));

      // Map to FY: Apr-Mar → if month >= 4, FY starts this year; else previous year
      let fyStart = y;
      if (m < 4) fyStart = y - 1;
      const fyEnd = fyStart + 1;
      fys.add(`${fyStart}-${fyEnd % 100}`);
    });

    return {
      fyOptions: Array.from(fys).sort().reverse(),
      calendarYearOptions: Array.from(years).sort().reverse(),
    };
  }, [allMonthlyRows]);

  // Reset selected values when mode changes
  const handleModeChange = (mode: PeriodMode) => {
    setPeriodMode(mode);
    setSelectedFYs([]);
    setSelectedYears([]);
  };

  // Auto-select latest option based on mode
  const fyYears = periodMode === 'fy_india'
    ? (selectedFYs.length > 0 ? selectedFYs : (fyOptions.length > 0 ? [fyOptions[0]] : []))
    : [];

  const calendarYears = periodMode === 'calendar'
    ? (selectedYears.length > 0 ? selectedYears : (calendarYearOptions.length > 0 ? [calendarYearOptions[0]] : []))
    : [];

  // Fetch filtered data with selected years
  const { clients, monthlyRows, invoicesByYM } = useClientData(rollingWindow, fyYears, calendarYears);

  // Derive KPI totals from filtered monthlyRows so they react to FY/year changes
  const derivedSummaryPeriod = useMemo(() => {
    if (!summaryPeriod) return null;
    const isFiltered = fyYears.length > 0 || calendarYears.length > 0;
    if (!isFiltered) return summaryPeriod;

    const totalInvoiced = monthlyRows.reduce((s, r) => s + r.totalInvoiced, 0);
    const newRevenue = monthlyRows.reduce((s, r) => s + r.newRevenue, 0);
    const repeatRevenue = monthlyRows.reduce((s, r) => s + r.repeatRevenue, 0);
    const pctNew = totalInvoiced > 0 ? (newRevenue / totalInvoiced) * 100 : 0;
    const pctRepeat = totalInvoiced > 0 ? (repeatRevenue / totalInvoiced) * 100 : 0;

    // Client counts from rolling window (not filterable by FY)
    const periodLabel = fyYears.length > 0
      ? `FY ${fyYears[0]}`
      : calendarYears.length > 0 ? calendarYears[0] : summaryPeriod.analysis_period_label;

    return {
      ...summaryPeriod,
      total_revenue_invoiced_usd: totalInvoiced,
      new_logo_revenue_invoiced_usd: newRevenue,
      repeat_revenue_invoiced_usd: repeatRevenue,
      pct_invoiced_revenue_new_logo: pctNew,
      pct_invoiced_revenue_repeat: pctRepeat,
      analysis_period_label: periodLabel,
    };
  }, [summaryPeriod, monthlyRows, fyYears, calendarYears]);

  return (
    <AppLayout>
      <ClientAnalytics
        clients={clients}
        summaryPeriod={derivedSummaryPeriod}
        monthlyRows={monthlyRows}
        invoicesByYM={invoicesByYM}
        loading={loading}
        rollingWindow={rollingWindow}
        onRollingWindowChange={setRollingWindow}
        periodMode={periodMode}
        onPeriodModeChange={handleModeChange}
        fyOptions={fyOptions}
        selectedFYs={fyYears}
        onFYsChange={setSelectedFYs}
        yearOptions={calendarYearOptions}
        selectedYears={calendarYears}
        onYearsChange={setSelectedYears}
      />
    </AppLayout>
  );
};

export default ClientAnalyticsPage;
